/**
 * Lightweight word-level diff for correction proposals.
 * Renders removed tokens in red-strikethrough, added tokens in green-highlight.
 *
 * Algorithm: LCS on tokens split at whitespace + basic punctuation.
 * O(n*m) memory — fine for question-sized strings (rarely >200 tokens).
 */
import React from "react";

function tokenize(s) {
  // Split KEEPING delimiters so re-joining looks natural (word/space/punct as tokens).
  if (!s) return [];
  return s.split(/(\s+|[.,;:!?()"„"''—–-])/).filter((t) => t !== "");
}

function lcs(a, b) {
  // Standard DP table (returns a matrix of lengths, then back-trace externally).
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = 1 + dp[i + 1][j + 1];
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  return dp;
}

function diffOps(a, b) {
  // Emit list of ops: {type: 'same'|'del'|'add', text}
  const dp = lcs(a, b);
  const ops = [];
  let i = 0, j = 0;
  const n = a.length, m = b.length;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: "same", text: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "del", text: a[i] });
      i++;
    } else {
      ops.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) { ops.push({ type: "del", text: a[i++] }); }
  while (j < m) { ops.push({ type: "add", text: b[j++] }); }
  return ops;
}

/**
 * Render a word-level diff between two strings.
 * - Removed: strikethrough on red background
 * - Added:   underline on green background
 * - Same:    neutral text
 *
 * Props:
 *   before: string
 *   after:  string
 *   testid: optional data-testid prefix
 */
export function InlineDiff({ before, after, testid }) {
  const a = tokenize(before || "");
  const b = tokenize(after || "");
  const ops = diffOps(a, b);
  // Merge consecutive same-type ops so DOM stays lean
  const merged = [];
  for (const op of ops) {
    const last = merged[merged.length - 1];
    if (last && last.type === op.type) last.text += op.text;
    else merged.push({ ...op });
  }
  return (
    <span data-testid={testid ? `${testid}-container` : undefined}
          className="font-mono text-[13px] leading-6">
      {merged.map((op, i) => {
        if (op.type === "same") {
          return <span key={i}>{op.text}</span>;
        }
        if (op.type === "del") {
          return (
            <span key={i}
                  className="bg-[#FEE2E2] text-[#7F1D1D] line-through decoration-2 px-0.5 rounded-sm"
                  data-testid={testid ? `${testid}-del` : undefined}>
              {op.text}
            </span>
          );
        }
        // add
        return (
          <span key={i}
                className="bg-[#D1FAE5] text-[#065F46] underline decoration-2 px-0.5 rounded-sm font-bold"
                data-testid={testid ? `${testid}-add` : undefined}>
            {op.text}
          </span>
        );
      })}
    </span>
  );
}

/** Compact one-line legend for the diff — put it once above the diffs. */
export function DiffLegend() {
  return (
    <div className="text-xs text-[#64748B] flex items-center gap-3">
      <span className="bg-[#FEE2E2] text-[#7F1D1D] line-through px-1 rounded-sm">usunięte</span>
      <span className="bg-[#D1FAE5] text-[#065F46] underline px-1 rounded-sm font-bold">dodane</span>
      <span>bez podświetlenia = bez zmian</span>
    </div>
  );
}
